import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ClipboardCheck, HardDrive, KeyRound, MonitorCheck, Plus, Save, Search, Trash2, TicketCheck, Wrench, X } from "lucide-react";
import { toast } from "sonner";
import { Button, ButtonLink, Card, FieldError, Input, Label, PageHeader, Textarea } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { api, ApiError } from "../lib/api";
import { useRouter } from "../lib/router";

type YesNo = "sim" | "nao" | "";

const BACKUP_ITENS = [
  "Documentos e arquivos do usuário",
  "E-mail (Outlook / PST)",
  "Favoritos do navegador",
  "Chaves de licença de programas",
  "Configurações de rede e impressoras",
];

const PROGRAMAS = [
  "Google Chrome",
  "Adobe Acrobat Reader",
  "Pacote Office",
  "Antivírus corporativo",
  "Zoom / Microsoft Teams",
  "Sistema Mira (ERP/Helpdesk)",
  "Compactador de arquivos (WinRAR/7zip)",
  "Driver de impressora",
];

const CREDENCIAIS_SUGERIDAS = [
  "Wi-Fi",
  "VPN",
  "Sistema / ERP",
  "Roteador / rede",
  "BIOS/UEFI",
];

const TESTES = [
  "Internet / rede",
  "Impressão",
  "Periféricos (mouse, teclado, webcam)",
  "Áudio e vídeo",
  "Desempenho geral do equipamento",
];

type FormState = {
  dataAtendimento: string;
  chamadoId: string;
  tecnico: string;
  empresaAtendida: string;
  usuarioEquipamento: string;
  marcaModelo: string;
  numeroSerie: string;
  patrimonio: string;
  motivoFormatacao: string;

  credenciais: { rotulo: string; usuario: string; senha: string }[];

  backupRealizado: YesNo;
  backupItens: string[];
  backupOutro: string;
  backupLocal: string;

  sistemaOperacional: string;
  ativacaoWindows: YesNo;
  windowsUpdateExecutado: YesNo;
  driversAtualizados: YesNo;
  antivirusInstalado: YesNo;

  programasInstalados: string[];
  programasOutro: string;

  emailConfigurado: YesNo;
  dadosRestaurados: YesNo;
  impressorasMapeadas: YesNo;
  usuarioDominioConfigurado: YesNo;

  testesFinais: string[];
  testesOutro: string;
  limpezaFisicaRealizada: YesNo;

  clienteConfirmouFuncionamento: YesNo;
  responsavelEntrega: string;
  observacoes: string;
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function emptyForm(tecnico: string): FormState {
  return {
    dataAtendimento: todayIso(),
    chamadoId: "",
    tecnico,
    empresaAtendida: "",
    usuarioEquipamento: "",
    marcaModelo: "",
    numeroSerie: "",
    patrimonio: "",
    motivoFormatacao: "",

    credenciais: [
      { rotulo: "Suporte (administrador local)", usuario: "suporte", senha: "" },
      { rotulo: "Usuário do cliente", usuario: "", senha: "" },
      { rotulo: "E-mail do cliente", usuario: "", senha: "" },
    ],

    backupRealizado: "",
    backupItens: [],
    backupOutro: "",
    backupLocal: "",

    sistemaOperacional: "",
    ativacaoWindows: "",
    windowsUpdateExecutado: "",
    driversAtualizados: "",
    antivirusInstalado: "",

    programasInstalados: [],
    programasOutro: "",

    emailConfigurado: "",
    dadosRestaurados: "",
    impressorasMapeadas: "",
    usuarioDominioConfigurado: "",

    testesFinais: [],
    testesOutro: "",
    limpezaFisicaRealizada: "",

    clienteConfirmouFuncionamento: "",
    responsavelEntrega: "",
    observacoes: "",
  };
}

function yesNoToBool(value: YesNo): boolean | null {
  if (value === "sim") return true;
  if (value === "nao") return false;
  return null;
}

function YesNoField({
  label,
  name,
  value,
  onChange,
}: {
  label: string;
  name: string;
  value: YesNo;
  onChange: (value: YesNo) => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="radio-grid radio-grid--compact">
        <label>
          <input type="radio" name={name} checked={value === "sim"} onChange={() => onChange("sim")} />
          <span>Sim</span>
        </label>
        <label>
          <input type="radio" name={name} checked={value === "nao"} onChange={() => onChange("nao")} />
          <span>Não</span>
        </label>
      </div>
    </div>
  );
}

export function ChecklistFormatacaoPage({ id }: { id?: string } = {}) {
  const { session } = useAuth();
  const { navigate } = useRouter();
  const [form, setForm] = useState<FormState>(() => emptyForm(session?.login ?? ""));
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [savedId, setSavedId] = useState<number | null>(id ? Number(id) : null);
  const [buscaChamado, setBuscaChamado] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState(true);
  const [chamadoListaAberta, setChamadoListaAberta] = useState(false);
  const [chamadoSelecionado, setChamadoSelecionado] = useState<{ numero: number; empresa: string; usuario: string; categoria: string } | null>(null);
  const chamadoBuscaRef = useRef<HTMLDivElement>(null);

  const categorias = useQuery({
    queryKey: ["categorias-checklist"],
    queryFn: api.getCategories,
    staleTime: 5 * 60 * 1000,
  });

  const subCategoriaIdsRelevantes = useMemo(() => {
    const nomesAlvo = ["formatação de máquina", "troca de computador", "configuração de novo funcionário"];
    return (categorias.data ?? [])
      .filter((item) => nomesAlvo.includes(item.nome_subcategoria.trim().toLowerCase()))
      .map((item) => item.id_subcategoria);
  }, [categorias.data]);

  const chamadosAbertos = useQuery({
    queryKey: ["chamados-em-espera", buscaChamado, filtroCategoria, subCategoriaIdsRelevantes],
    queryFn: () =>
      api.listTickets(0, {
        onlyOpen: true,
        status: "TODOS",
        type: "Todos",
        query: buscaChamado,
        pageSize: 15,
        subCategoriaIds: filtroCategoria && subCategoriaIdsRelevantes.length > 0 ? subCategoriaIdsRelevantes : undefined,
      }),
    enabled: !categorias.isLoading,
  });

  const opcoesChamados = useMemo(() => chamadosAbertos.data?.itens ?? [], [chamadosAbertos.data]);

  useEffect(() => {
    function handleClickFora(event: MouseEvent) {
      if (chamadoBuscaRef.current && !chamadoBuscaRef.current.contains(event.target as Node)) {
        setChamadoListaAberta(false);
      }
    }
    document.addEventListener("mousedown", handleClickFora);
    return () => document.removeEventListener("mousedown", handleClickFora);
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelado = false;
    (async () => {
      try {
        const rows = await api.getChecklistFormatacao(Number(id));
        const item = rows?.[0];
        if (!item || cancelado) return;
        const parseList = (value: string | null): string[] => {
          if (!value) return [];
          try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        };
        const parseCredenciais = (value: string | null): { rotulo: string; usuario: string; senha: string }[] => {
          if (!value) return [];
          try {
            const parsed = JSON.parse(value);
            if (!Array.isArray(parsed)) return [];
            // Defensivo: garante que todo item tem os 3 campos como string,
            // mesmo que um registro antigo/incompleto tenha vindo faltando algum.
            return parsed.map((item) => ({
              rotulo: typeof item?.rotulo === "string" ? item.rotulo : "",
              usuario: typeof item?.usuario === "string" ? item.usuario : "",
              senha: typeof item?.senha === "string" ? item.senha : "",
            }));
          } catch {
            return [];
          }
        };
        const boolToYesNo = (value: boolean | null): YesNo => (value === true ? "sim" : value === false ? "nao" : "");

        setSavedId(item.Id);
        setForm({
          dataAtendimento: item.DataAtendimento ? item.DataAtendimento.slice(0, 10) : todayIso(),
          chamadoId: item.ChamadoId ? String(item.ChamadoId) : "",
          tecnico: item.Tecnico ?? "",
          empresaAtendida: item.EmpresaAtendida ?? "",
          usuarioEquipamento: item.UsuarioEquipamento ?? "",
          marcaModelo: item.MarcaModelo ?? "",
          numeroSerie: item.NumeroSerie ?? "",
          patrimonio: item.Patrimonio ?? "",
          motivoFormatacao: item.MotivoFormatacao ?? "",

          credenciais: parseCredenciais(item.Credenciais),

          backupRealizado: boolToYesNo(item.BackupRealizado),
          backupItens: parseList(item.BackupItens),
          backupOutro: item.BackupOutro ?? "",
          backupLocal: item.BackupLocal ?? "",

          sistemaOperacional: item.SistemaOperacional ?? "",
          ativacaoWindows: boolToYesNo(item.AtivacaoWindows),
          windowsUpdateExecutado: boolToYesNo(item.WindowsUpdateExecutado),
          driversAtualizados: boolToYesNo(item.DriversAtualizados),
          antivirusInstalado: boolToYesNo(item.AntivirusInstalado),

          programasInstalados: parseList(item.ProgramasInstalados),
          programasOutro: item.ProgramasOutro ?? "",

          emailConfigurado: boolToYesNo(item.EmailConfigurado),
          dadosRestaurados: boolToYesNo(item.DadosRestaurados),
          impressorasMapeadas: boolToYesNo(item.ImpressorasMapeadas),
          usuarioDominioConfigurado: boolToYesNo(item.UsuarioDominioConfigurado),

          testesFinais: parseList(item.TestesFinais),
          testesOutro: item.TestesOutro ?? "",
          limpezaFisicaRealizada: boolToYesNo(item.LimpezaFisicaRealizada),

          clienteConfirmouFuncionamento: boolToYesNo(item.ClienteConfirmouFuncionamento),
          responsavelEntrega: item.ResponsavelEntrega ?? "",
          observacoes: item.Observacoes ?? "",
        });
      } catch {
        toast.error("Não foi possível carregar o checklist salvo.");
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [id]);

  async function selecionarChamado(ticket: { numero: number; empresa: string; usuario: string; categoria: string }) {
    setChamadoListaAberta(false);
    setChamadoSelecionado(ticket);
    setForm((current) => ({
      ...current,
      chamadoId: String(ticket.numero),
      empresaAtendida: ticket.empresa || current.empresaAtendida,
      usuarioEquipamento: ticket.usuario || current.usuarioEquipamento,
    }));
    if (errors.chamadoId) setErrors((current) => ({ ...current, chamadoId: undefined }));

    // Evita duplicar checklist: se já existe um (rascunho ou concluído) vinculado a esse
    // chamado, continua editando o mesmo protocolo em vez de deixar criar outro do zero.
    if (!savedId) {
      try {
        const existentes = await api.getChecklistByChamado(ticket.numero);
        const existente = existentes?.[0];
        if (existente) {
          toast.info(
            existente.status === "rascunho"
              ? `Já existe um rascunho (#${existente.Id}) para este chamado. Continuando de onde parou.`
              : `Este chamado já tem um checklist concluído (#${existente.Id}). Abrindo para consulta/edição.`,
          );
          navigate(`/checklist-formatacao/${existente.Id}/editar`);
        }
      } catch {
        // Falha na verificação não deve travar o fluxo normal de criação.
      }
    }
  }

  function limparChamadoSelecionado() {
    setChamadoSelecionado(null);
    setField("chamadoId", "");
  }

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    if (errors[key]) setErrors((current) => ({ ...current, [key]: undefined }));
  }

  function toggleItem(list: "backupItens" | "programasInstalados" | "testesFinais", item: string) {
    setForm((current) => {
      const has = current[list].includes(item);
      return {
        ...current,
        [list]: has ? current[list].filter((value) => value !== item) : [...current[list], item],
      };
    });
  }

  function updateCredencial(index: number, field: "rotulo" | "usuario" | "senha", value: string) {
    setForm((current) => ({
      ...current,
      credenciais: current.credenciais.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }));
  }

  function addCredencial() {
    setForm((current) => ({
      ...current,
      credenciais: [...current.credenciais, { rotulo: "", usuario: "", senha: "" }],
    }));
  }

  function addCredencialPreset(rotulo: string) {
    setForm((current) => {
      if (current.credenciais.some((item) => item.rotulo === rotulo)) return current;
      return {
        ...current,
        credenciais: [...current.credenciais, { rotulo, usuario: "", senha: "" }],
      };
    });
  }

  function removeCredencial(index: number) {
    setForm((current) => ({
      ...current,
      credenciais: current.credenciais.filter((_, i) => i !== index),
    }));
  }

  function validate() {
    const nextErrors: Record<string, string> = {};
    if (!form.dataAtendimento) nextErrors.dataAtendimento = "Informe a data do atendimento.";
    if (!form.tecnico.trim()) nextErrors.tecnico = "Informe o técnico responsável.";
    if (!form.usuarioEquipamento.trim())
      nextErrors.usuarioEquipamento = "Informe o usuário/equipamento atendido.";
    if (!form.motivoFormatacao.trim())
      nextErrors.motivoFormatacao = "Informe o motivo da formatação.";
    if (form.chamadoId.trim() && !/^\d+$/.test(form.chamadoId.trim()))
      nextErrors.chamadoId = "Informe apenas números.";

    const credencialIncompleta = form.credenciais.some(
      (item) => ((item.usuario ?? "").trim() || (item.senha ?? "").trim()) && !(item.rotulo ?? "").trim(),
    );
    if (credencialIncompleta) nextErrors.credenciais = "Informe a identificação de cada usuário/senha preenchido.";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function buildPayload() {
    return {
      dataAtendimento: form.dataAtendimento,
      chamadoId: form.chamadoId.trim() ? Number(form.chamadoId.trim()) : null,
      tecnico: form.tecnico,
      empresaAtendida: form.empresaAtendida || null,
      usuarioEquipamento: form.usuarioEquipamento,
      marcaModelo: form.marcaModelo || null,
      numeroSerie: form.numeroSerie || null,
      patrimonio: form.patrimonio || null,
      motivoFormatacao: form.motivoFormatacao,

      credenciais: form.credenciais.filter(
        (item) => (item.rotulo ?? "").trim() || (item.usuario ?? "").trim() || (item.senha ?? "").trim(),
      ),

      backupRealizado: yesNoToBool(form.backupRealizado),
      backupItens: form.backupItens.length > 0 ? form.backupItens : null,
      backupOutro: form.backupOutro || null,
      backupLocal: form.backupLocal || null,

      sistemaOperacional: form.sistemaOperacional || null,
      ativacaoWindows: yesNoToBool(form.ativacaoWindows),
      windowsUpdateExecutado: yesNoToBool(form.windowsUpdateExecutado),
      driversAtualizados: yesNoToBool(form.driversAtualizados),
      antivirusInstalado: yesNoToBool(form.antivirusInstalado),

      programasInstalados: form.programasInstalados.length > 0 ? form.programasInstalados : null,
      programasOutro: form.programasOutro || null,

      emailConfigurado: yesNoToBool(form.emailConfigurado),
      dadosRestaurados: yesNoToBool(form.dadosRestaurados),
      impressorasMapeadas: yesNoToBool(form.impressorasMapeadas),
      usuarioDominioConfigurado: yesNoToBool(form.usuarioDominioConfigurado),

      testesFinais: form.testesFinais.length > 0 ? form.testesFinais : null,
      testesOutro: form.testesOutro || null,
      limpezaFisicaRealizada: yesNoToBool(form.limpezaFisicaRealizada),

      clienteConfirmouFuncionamento: yesNoToBool(form.clienteConfirmouFuncionamento),
      responsavelEntrega: form.responsavelEntrega || null,
      observacoes: form.observacoes || null,
    };
  }

  async function salvarRascunho() {
    setSavingDraft(true);
    try {
      const payload = buildPayload();
      const result = savedId
        ? await api.updateChecklistFormatacao(savedId, payload, "rascunho")
        : await api.submitChecklistFormatacao(payload, "rascunho");
      setSavedId(result.id);
      toast.success(`Rascunho salvo! Protocolo #${result.id}. Você pode continuar preenchendo depois.`);
    } catch (error) {
      console.error("Falha ao salvar rascunho do checklist:", error);
      toast.error(
        error instanceof ApiError
          ? error.message
          : "Não foi possível salvar o rascunho. Tente novamente.",
      );
    } finally {
      setSavingDraft(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!validate()) {
      toast.error("Revise os campos obrigatórios destacados.");
      return;
    }

    setLoading(true);
    try {
      const payload = buildPayload();
      const result = savedId
        ? await api.updateChecklistFormatacao(savedId, payload, "concluido")
        : await api.submitChecklistFormatacao(payload, "concluido");
      toast.success(`Checklist registrado! Protocolo #${result.id}.`);
      navigate("/");
    } catch (error) {
      console.error("Falha ao concluir o checklist:", error);
      toast.error(
        error instanceof ApiError
          ? error.message
          : "Não foi possível registrar o checklist. Tente novamente.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page-stack max-w-[960px]">
      <PageHeader
        eyebrow="Equipe Mira Sistemas"
        title="Checklist de formatação de máquinas"
        description="Preencha este checklist a cada formatação realizada. Uso restrito à equipe interna da Mira Sistemas."
        actions={
          <ButtonLink to="/checklist-formatacao/lista" variant="secondary">
            Ver checklists registrados
          </ButtonLink>
        }
      />

      <form onSubmit={submit} className="space-y-5">
        <Card className="form-section">
          <div className="form-section-heading">
            <span>01</span>
            <div>
              <h2>Identificação do atendimento</h2>
              <p>Dados do equipamento e do técnico responsável.</p>
            </div>
          </div>

          <div className="mb-4">
            <Label htmlFor="cf-busca-chamado">
              <TicketCheck className="inline size-[14px]" /> Puxar de um chamado em espera
            </Label>

            {chamadoSelecionado ? (
              <div className="chamado-selecionado">
                <CheckCircle2 className="size-4 shrink-0 text-emerald-600" />
                <div className="min-w-0">
                  <strong>#{chamadoSelecionado.numero}</strong> — {chamadoSelecionado.empresa} — {chamadoSelecionado.usuario}
                  <div className="text-xs text-slate-400">{chamadoSelecionado.categoria}</div>
                </div>
                <button type="button" onClick={limparChamadoSelecionado} aria-label="Remover seleção" title="Remover seleção">
                  <X className="size-4" />
                </button>
              </div>
            ) : (
              <div ref={chamadoBuscaRef} className="relative">
                <div className="relative">
                  <Input
                    id="cf-busca-chamado"
                    placeholder="Buscar por empresa, usuário ou nº do chamado..."
                    value={buscaChamado}
                    onFocus={() => setChamadoListaAberta(true)}
                    onChange={(event) => {
                      setBuscaChamado(event.target.value);
                      setChamadoListaAberta(true);
                    }}
                  />
                  <Search className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-slate-300" />
                </div>
                {subCategoriaIdsRelevantes.length > 0 && (
                  <label className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                    <input
                      type="checkbox"
                      checked={filtroCategoria}
                      onChange={(event) => setFiltroCategoria(event.target.checked)}
                    />
                    Mostrar só Formatação / Troca de computador / Configuração de novo funcionário
                  </label>
                )}
                {chamadoListaAberta && (
                  <div className="chamado-lista chamado-lista--dropdown">
                    {chamadosAbertos.isLoading ? (
                      <p className="p-3 text-xs text-slate-400">Carregando chamados em espera...</p>
                    ) : opcoesChamados.length === 0 ? (
                      <p className="p-3 text-xs text-slate-400">Nenhum chamado em aberto encontrado.</p>
                    ) : (
                      <>
                        <p className="border-b border-[var(--line)] bg-slate-50 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                          {opcoesChamados.length} chamado{opcoesChamados.length === 1 ? "" : "s"} em espera
                        </p>
                        {opcoesChamados.map((ticket) => (
                          <button
                            type="button"
                            key={ticket.numero}
                            className="chamado-lista-item"
                            onClick={() =>
                              selecionarChamado({
                                numero: ticket.numero,
                                empresa: ticket.empresa,
                                usuario: ticket.usuario,
                                categoria: ticket.categoria,
                              })
                            }
                          >
                            <span className="font-medium">#{ticket.numero}</span>
                            <span className="truncate">{ticket.empresa} — {ticket.usuario}</span>
                            <span className="ml-auto shrink-0 text-xs text-slate-400">{ticket.ultimoStatus}</span>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-grid">
            <div>
              <Label htmlFor="cf-data">Data do atendimento *</Label>
              <Input
                id="cf-data"
                type="date"
                value={form.dataAtendimento}
                onChange={(event) => setField("dataAtendimento", event.target.value)}
                aria-invalid={Boolean(errors.dataAtendimento)}
              />
              <FieldError>{errors.dataAtendimento}</FieldError>
            </div>
            <div>
              <Label htmlFor="cf-tecnico">Técnico responsável *</Label>
              <Input
                id="cf-tecnico"
                value={form.tecnico}
                onChange={(event) => setField("tecnico", event.target.value)}
                aria-invalid={Boolean(errors.tecnico)}
              />
              <FieldError>{errors.tecnico}</FieldError>
            </div>
            <div>
              <Label htmlFor="cf-chamado">Nº do chamado (se houver)</Label>
              <Input
                id="cf-chamado"
                inputMode="numeric"
                value={form.chamadoId}
                onChange={(event) => setField("chamadoId", event.target.value)}
                aria-invalid={Boolean(errors.chamadoId)}
              />
              <FieldError>{errors.chamadoId}</FieldError>
            </div>
            <div>
              <Label htmlFor="cf-empresa">Empresa atendida</Label>
              <Input
                id="cf-empresa"
                placeholder="Cliente atendido"
                value={form.empresaAtendida}
                onChange={(event) => setField("empresaAtendida", event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="cf-usuario">Usuário / equipamento atendido *</Label>
              <Input
                id="cf-usuario"
                placeholder="Ex.: João da Silva - Notebook Recepção"
                value={form.usuarioEquipamento}
                onChange={(event) => setField("usuarioEquipamento", event.target.value)}
                aria-invalid={Boolean(errors.usuarioEquipamento)}
              />
              <FieldError>{errors.usuarioEquipamento}</FieldError>
            </div>
            <div>
              <Label htmlFor="cf-marca">Marca / modelo</Label>
              <Input
                id="cf-marca"
                value={form.marcaModelo}
                onChange={(event) => setField("marcaModelo", event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="cf-serie">Número de série</Label>
              <Input
                id="cf-serie"
                value={form.numeroSerie}
                onChange={(event) => setField("numeroSerie", event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="cf-patrimonio">Patrimônio</Label>
              <Input
                id="cf-patrimonio"
                value={form.patrimonio}
                onChange={(event) => setField("patrimonio", event.target.value)}
              />
            </div>
          </div>
          <div className="mt-4">
            <Label htmlFor="cf-motivo">Motivo da formatação *</Label>
            <Textarea
              id="cf-motivo"
              value={form.motivoFormatacao}
              onChange={(event) => setField("motivoFormatacao", event.target.value)}
              aria-invalid={Boolean(errors.motivoFormatacao)}
            />
            <FieldError>{errors.motivoFormatacao}</FieldError>
          </div>
        </Card>

        <Card className="form-section">
          <div className="form-section-heading">
            <span>02</span>
            <div>
              <h2><KeyRound className="inline size-[18px]" /> Usuários e senhas configurados</h2>
              <p>
                Registre todos os acessos deixados na máquina. Por padrão sempre incluímos o usuário de
                suporte (administrador local da Mira) e o usuário do cliente.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {form.credenciais.map((item, index) => (
              <div key={index} className="checklist-credencial-row">
                <div>
                  <Label htmlFor={`cred-rotulo-${index}`}>Identificação</Label>
                  <Input
                    id={`cred-rotulo-${index}`}
                    placeholder="Ex.: Suporte (admin local), Usuário do cliente..."
                    value={item.rotulo}
                    onChange={(event) => updateCredencial(index, "rotulo", event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor={`cred-usuario-${index}`}>Usuário</Label>
                  <Input
                    id={`cred-usuario-${index}`}
                    value={item.usuario}
                    onChange={(event) => updateCredencial(index, "usuario", event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor={`cred-senha-${index}`}>Senha</Label>
                  <Input
                    id={`cred-senha-${index}`}
                    value={item.senha}
                    onChange={(event) => updateCredencial(index, "senha", event.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeCredencial(index)}
                  aria-label="Remover usuário"
                  title="Remover usuário"
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
            <FieldError>{errors.credenciais}</FieldError>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={addCredencial}>
                <Plus className="size-4" />
                Adicionar usuário
              </Button>
              <span className="text-xs text-slate-400">Atalhos:</span>
              {CREDENCIAIS_SUGERIDAS.map((rotulo) => (
                <button
                  key={rotulo}
                  type="button"
                  className="chip-button"
                  onClick={() => addCredencialPreset(rotulo)}
                >
                  + {rotulo}
                </button>
              ))}
            </div>
          </div>
        </Card>

        <Card className="form-section">
          <div className="form-section-heading">
            <span>03</span>
            <div>
              <h2><HardDrive className="inline size-[18px]" /> Backup antes da formatação</h2>
              <p>Confirme o que foi salvo antes de iniciar o processo.</p>
            </div>
          </div>
          <YesNoField
            label="Foi realizado backup dos dados do usuário?"
            name="backupRealizado"
            value={form.backupRealizado}
            onChange={(value) => setField("backupRealizado", value)}
          />
          <div className="mt-4">
            <Label>O que foi incluído no backup?</Label>
            <div className="checkbox-grid">
              {BACKUP_ITENS.map((item) => (
                <label key={item}>
                  <input
                    type="checkbox"
                    checked={form.backupItens.includes(item)}
                    onChange={() => toggleItem("backupItens", item)}
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>
            <Input
              className="mt-2"
              placeholder="Outro (especifique)"
              value={form.backupOutro}
              onChange={(event) => setField("backupOutro", event.target.value)}
            />
          </div>
          <div className="mt-4">
            <Label htmlFor="cf-backup-local">Onde o backup foi salvo</Label>
            <Input
              id="cf-backup-local"
              placeholder="Ex.: HD externo, nuvem, servidor local..."
              value={form.backupLocal}
              onChange={(event) => setField("backupLocal", event.target.value)}
            />
          </div>
        </Card>

        <Card className="form-section">
          <div className="form-section-heading">
            <span>04</span>
            <div>
              <h2><Wrench className="inline size-[18px]" /> Instalação do sistema</h2>
              <p>Sistema operacional, ativação e itens básicos de segurança.</p>
            </div>
          </div>
          <div className="form-grid">
            <div>
              <Label htmlFor="cf-so">Sistema operacional instalado</Label>
              <Input
                id="cf-so"
                placeholder="Ex.: Windows 11 Pro 23H2"
                value={form.sistemaOperacional}
                onChange={(event) => setField("sistemaOperacional", event.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <YesNoField
              label="Windows ativado"
              name="ativacaoWindows"
              value={form.ativacaoWindows}
              onChange={(value) => setField("ativacaoWindows", value)}
            />
            <YesNoField
              label="Windows Update executado"
              name="windowsUpdateExecutado"
              value={form.windowsUpdateExecutado}
              onChange={(value) => setField("windowsUpdateExecutado", value)}
            />
            <YesNoField
              label="Drivers instalados/atualizados"
              name="driversAtualizados"
              value={form.driversAtualizados}
              onChange={(value) => setField("driversAtualizados", value)}
            />
            <YesNoField
              label="Antivírus instalado"
              name="antivirusInstalado"
              value={form.antivirusInstalado}
              onChange={(value) => setField("antivirusInstalado", value)}
            />
          </div>
          <div className="mt-4">
            <Label>Programas reinstalados</Label>
            <div className="checkbox-grid">
              {PROGRAMAS.map((item) => (
                <label key={item}>
                  <input
                    type="checkbox"
                    checked={form.programasInstalados.includes(item)}
                    onChange={() => toggleItem("programasInstalados", item)}
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>
            <Input
              className="mt-2"
              placeholder="Outro (especifique)"
              value={form.programasOutro}
              onChange={(event) => setField("programasOutro", event.target.value)}
            />
          </div>
        </Card>

        <Card className="form-section">
          <div className="form-section-heading">
            <span>05</span>
            <div>
              <h2><MonitorCheck className="inline size-[18px]" /> Restauração e configuração</h2>
              <p>Dados, e-mail, rede e domínio de volta ao normal.</p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <YesNoField
              label="E-mail configurado"
              name="emailConfigurado"
              value={form.emailConfigurado}
              onChange={(value) => setField("emailConfigurado", value)}
            />
            <YesNoField
              label="Dados do backup restaurados"
              name="dadosRestaurados"
              value={form.dadosRestaurados}
              onChange={(value) => setField("dadosRestaurados", value)}
            />
            <YesNoField
              label="Impressoras/rede mapeadas"
              name="impressorasMapeadas"
              value={form.impressorasMapeadas}
              onChange={(value) => setField("impressorasMapeadas", value)}
            />
            <YesNoField
              label="Usuário de domínio/rede configurado"
              name="usuarioDominioConfigurado"
              value={form.usuarioDominioConfigurado}
              onChange={(value) => setField("usuarioDominioConfigurado", value)}
            />
          </div>
        </Card>

        <Card className="form-section">
          <div className="form-section-heading">
            <span>06</span>
            <div>
              <h2><ClipboardCheck className="inline size-[18px] " /> Testes finais e entrega</h2>
              <p>Validações antes de devolver o equipamento.</p>
            </div>
          </div>
          <div className="checkbox-grid">
            {TESTES.map((item) => (
              <label key={item}>
                <input
                  type="checkbox"
                  checked={form.testesFinais.includes(item)}
                  onChange={() => toggleItem("testesFinais", item)}
                />
                <span>{item}</span>
              </label>
            ))}
          </div>
          <Input
            className="mt-2"
            placeholder="Outro teste (especifique)"
            value={form.testesOutro}
            onChange={(event) => setField("testesOutro", event.target.value)}
          />

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <YesNoField
              label="Limpeza física realizada"
              name="limpezaFisicaRealizada"
              value={form.limpezaFisicaRealizada}
              onChange={(value) => setField("limpezaFisicaRealizada", value)}
            />
            <YesNoField
              label="Cliente confirmou o funcionamento"
              name="clienteConfirmouFuncionamento"
              value={form.clienteConfirmouFuncionamento}
              onChange={(value) => setField("clienteConfirmouFuncionamento", value)}
            />
          </div>

          <div className="mt-4">
            <Label htmlFor="cf-responsavel">Responsável pela entrega do equipamento</Label>
            <Input
              id="cf-responsavel"
              value={form.responsavelEntrega}
              onChange={(event) => setField("responsavelEntrega", event.target.value)}
            />
          </div>

          <div className="mt-4">
            <Label htmlFor="cf-obs">Observações gerais</Label>
            <Textarea
              id="cf-obs"
              value={form.observacoes}
              onChange={(event) => setField("observacoes", event.target.value)}
            />
          </div>
        </Card>

        <Card className="form-section flex flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            {savedId
              ? `Rascunho #${savedId} em andamento. Você pode salvar o progresso a qualquer momento.`
              : "O checklist ficará registrado e vinculado ao chamado, quando informado."}
          </p>
          <div className="flex flex-row items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              disabled={savingDraft || loading}
              onClick={salvarRascunho}
            >
              <Save className="size-4" />
              {savingDraft ? "Salvando..." : "Salvar rascunho"}
            </Button>
            <Button type="submit" size="lg" disabled={loading || savingDraft}>
              <ClipboardCheck className="size-4" />
              {loading ? "Registrando..." : "Concluir checklist"}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
